import { useEffect, useMemo, useState } from "react";
import CalendarNav from "./CalendarNav";
import { statusClass, eventClass } from "@/lib/utils2";
import api from "@/lib/api";

const WEEK_DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function buildMonth(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  const dow = (first.getDay() + 6) % 7; // Mon=0
  start.setDate(first.getDate() - dow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}
function buildWeek(anchor) {
  const start = new Date(anchor);
  const dow = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dow);
  start.setHours(0, 0, 0, 0);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function iso(d) {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

export default function HolidayCalendarView({ requests = [], onOpen, jobRoles = [], activeRole = "", onRoleClick, onDayClick, bankHolidays = [] }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [view, setView] = useState("month");

  // Load full contractor directory so we can show total-per-role and names.
  // Silently skipped for supervisor scope where /employees isn't accessible.
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/employees").then(({ data }) => {
      if (!cancelled) setEmployees(Array.isArray(data) ? data : []);
    }).catch(() => { /* non-admin — safe to ignore */ });
    return () => { cancelled = true; };
  }, []);

  const days = useMemo(
    () => (view === "week" ? buildWeek(cursor) : buildMonth(cursor.getFullYear(), cursor.getMonth())),
    [cursor, view]
  );

  // Requests overlapping the current visible month (any approved or pending).
  const monthStart = view === "week" ? days[0] : new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = view === "week" ? days[days.length - 1] : new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const inMonth = useMemo(() => requests.filter((r) => {
    const s = new Date(r.start_date), e = new Date(r.end_date);
    return s <= monthEnd && e >= monthStart && (r.status === "approved" || r.status === "pending");
  }), [requests, cursor, view]);

  // Bank holiday map (date string → name)
  const bhMap = useMemo(() => {
    const m = new Map();
    for (const b of bankHolidays || []) m.set(b.date, b.name);
    return m;
  }, [bankHolidays]);

  // Build per-role aggregation.
  // For each role we compute:
  //   - the full set of contractors that hold this role (primary/secondary/third or job_role fallback)
  //   - the subset currently on absence this month
  // Counts include a contractor under every role they carry.
  const roleAgg = useMemo(() => {
    // Dedup key = lowercased full name ONLY.
    // /api/employees has email=null for the whole directory (Wayne's Excel had
    // no emails), while /api/holiday-requests carries real emails — so email
    // can never bridge the two sides. Name is the only field present on both,
    // and Wayne's list has no exact-name collisions among the 235 rows.
    const keyOf = (first, last, fullName) => {
      const s = `${(first || "").toLowerCase()} ${(last || "").toLowerCase()}`.trim();
      return s || (fullName || "").toLowerCase().trim();
    };

    // Which contractors are on absence this month.
    const onAbsence = new Map(); // key -> { name, email, roles[] }
    for (const r of inMonth) {
      const key = keyOf(r.first_name, r.last_name);
      if (!key) continue;
      const roles = (r.employee_roles && r.employee_roles.length)
        ? r.employee_roles
        : (r.job_role ? [r.job_role] : []);
      if (!onAbsence.has(key)) {
        onAbsence.set(key, {
          name: `${r.first_name} ${r.last_name}`.trim(),
          email: r.email || "",
          roles,
        });
      }
    }

    // Group contractors by role from the directory.
    const empByRole = new Map();  // role -> Map<key, {name, email}>
    const roleOf = (e) => {
      const out = new Set();
      const push = (v) => { if (v && String(v).trim()) out.add(String(v).trim()); };
      if (Array.isArray(e.job_roles)) e.job_roles.forEach(push);
      push(e.primary_role);
      if (Array.isArray(e.secondary_roles)) e.secondary_roles.forEach(push);
      push(e.third_role);
      push(e.job_role);
      return [...out];
    };
    for (const e of employees) {
      const roles = roleOf(e);
      const label = `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.full_name || e.email || "—";
      const key = keyOf(e.first_name, e.last_name, e.full_name);
      if (!key) continue;
      for (const role of roles) {
        if (!empByRole.has(role)) empByRole.set(role, new Map());
        if (!empByRole.get(role).has(key)) {
          empByRole.get(role).set(key, { name: label, email: e.email || "" });
        }
      }
    }

    return jobRoles.map((role) => {
      const byKey = new Map(empByRole.get(role) || []);
      // Merge in any on-absence contractors that aren't already in the
      // directory. Because both sides now key by lowercased name, the same
      // person WILL be found by byKey.has() and NOT be inserted twice.
      for (const [k, info] of onAbsence.entries()) {
        if (info.roles.includes(role) && !byKey.has(k)) {
          byKey.set(k, { name: info.name, email: info.email });
        }
      }
      const offKeysForRole = new Set();
      for (const [k, info] of onAbsence.entries()) {
        if (info.roles.includes(role)) offKeysForRole.add(k);
      }
      const all = [...byKey.entries()]
        .map(([k, v]) => ({ key: k, name: v.name, email: v.email }))
        .sort((a, b) => a.name.localeCompare(b.name));
      return {
        role,
        total: all.length,
        offCount: all.filter((c) => offKeysForRole.has(c.key)).length,
        contractors: all.map((c) => ({
          key: c.key,
          name: c.name,
          onAbsence: offKeysForRole.has(c.key),
        })),
      };
    });
  }, [inMonth, jobRoles, employees]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const r of requests) {
      const s = new Date(r.start_date), e = new Date(r.end_date);
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const k = iso(d);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(r);
      }
    }
    return map;
  }, [requests]);

  const todayIso = iso(today);

  return (
    <div className="space-y-3" data-testid="holiday-calendar">
      <CalendarNav
        cursor={cursor}
        onCursor={setCursor}
        view={view}
        onView={setView}
        label={view === "week"
          ? `Week of ${days[0].toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`
          : cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
      />

      {/* Role summary panel */}
      {jobRoles.length > 0 && view === "month" && (
        <div className="border border-border rounded-lg bg-[#1E1E1E] p-4" data-testid="role-summary-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground font-semibold">On absence this month</div>
              <div className="text-[11px] text-muted-foreground">Contractors listed under every role they hold (primary, secondary &amp; third). Click a role name to filter the calendar.</div>
            </div>
            {activeRole && (
              <button
                data-testid="role-summary-clear"
                onClick={() => onRoleClick?.(activeRole)}
                className="text-xs text-primary hover:underline whitespace-nowrap"
              >Clear role filter ({activeRole})</button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {roleAgg.map(({ role, total, offCount, contractors }) => {
              const isActive = activeRole === role;
              return (
                <div
                  key={role}
                  data-testid={`role-summary-${role}`}
                  className={`rounded-md border transition-colors ${
                    isActive
                      ? "border-primary bg-accent/60"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {/* Clickable header row = filter toggle */}
                  <button
                    onClick={() => onRoleClick?.(role)}
                    className="w-full text-left px-3 pt-3 pb-2 rounded-t-md hover:bg-accent/40"
                    data-testid={`role-summary-header-${role}`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold truncate">{role}</div>
                      <div className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">{total} total</div>
                    </div>
                    <div className="mt-1 flex items-baseline gap-1.5">
                      <span className={`font-heading text-2xl font-semibold tabular-nums ${offCount > 0 ? "text-foreground" : "text-muted-foreground/50"}`}>
                        {offCount}
                      </span>
                      <span className="text-[11px] text-muted-foreground">on absence</span>
                    </div>
                  </button>

                  {/* Contractor names — always visible */}
                  <div className="px-3 pb-3 pt-1 border-t border-border/60">
                    {total === 0 ? (
                      <div className="text-[11px] text-muted-foreground/70 italic">No contractors in this role.</div>
                    ) : (
                      <ul className="text-[12px] leading-[1.5] max-h-40 overflow-y-auto space-y-0.5" data-testid={`role-summary-names-${role}`}>
                        {contractors.map((c) => (
                          <li
                            key={c.key}
                            className={
                              c.onAbsence
                                ? "text-foreground font-medium flex items-center gap-1.5"
                                : "text-muted-foreground"
                            }
                            title={c.onAbsence ? `${c.name} — on absence this month` : c.name}
                          >
                            {c.onAbsence && (
                              <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"/>
                            )}
                            <span className="truncate">{c.name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-7 text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold mb-1">
        {WEEK_DAYS.map((d) => <div key={d} className="px-2">{d}</div>)}
      </div>

      <div className={view === "week" ? "cal-grid cal-grid-week" : "cal-grid"}>
        {days.map((d, i) => {
          const k = iso(d);
          const isOther = view === "month" ? d.getMonth() !== cursor.getMonth() : false;
          const isToday = k === todayIso;
          const list = byDay.get(k) || [];
          const cellClickable = !!onDayClick;
          const bhName = bhMap.get(k);
          return (
            <div
              key={i}
              data-testid={`cal-cell-${k}`}
              title={bhName ? `Bank holiday — ${bhName}` : undefined}
              className={`cal-cell ${isOther ? "other-month" : ""} ${isToday ? "today" : ""} ${bhName ? "bank-holiday" : ""} ${cellClickable ? "cursor-pointer hover:bg-accent/40 transition-colors" : ""}`}
              onClick={cellClickable ? () => onDayClick(d, list) : undefined}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs ${isOther ? "" : "font-semibold"}`}>{d.getDate()}</span>
                <span className="flex items-center gap-1">
                  {bhName && <span data-testid={`cal-bh-${k}`} className="text-[9px] uppercase font-bold text-purple-700">BH</span>}
                  {list.length > 0 && <span className="text-[10px] text-muted-foreground tabular-nums">{list.length}</span>}
                </span>
              </div>
              {bhName && (
                <div className="text-[10px] text-purple-700 font-medium truncate mb-1">{bhName}</div>
              )}
              <div className="space-y-1 overflow-hidden">
                {list.slice(0, 3).map((r) => (
                  <button
                    key={r.id + k}
                    data-testid={`cal-event-${r.id}`}
                    onClick={(e) => { e.stopPropagation(); onOpen?.(r); }}
                    className={`block w-full text-left text-[11px] px-1.5 py-0.5 rounded ${eventClass(r)} truncate hover:opacity-90`}
                    title={`${r.first_name} ${r.last_name} · ${r.department} · ${(r.request_type || "").toLowerCase() === "sickness" ? "Sickness" : r.status}`}
                  >
                    {r.first_name} {r.last_name[0]}.
                  </button>
                ))}
                {list.length > 3 && <div className="text-[10px] text-muted-foreground pl-1">+{list.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-2">
        {["pending","approved","rejected","cancelled"].map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${statusClass(s)}`}></span>
            {s}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm status-sickness"/>
          sickness
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-purple-100 border border-purple-300"/>
          bank holiday
        </span>
      </div>
    </div>
  );
}
